import { useState, useEffect } from 'react';
import Hero from './components/Hero';
import ServiceSelection from './components/ServiceSelection';
import EmergencyDiagnosis from './components/EmergencyDiagnosis';
import CaseStudies from './components/CaseStudies';
import ProcessGuide from './components/ProcessGuide';
import WhyNow from './components/WhyNow';
import OldHousingSupport from './components/OldHousingSupport';
import ConsultationForm from './components/ConsultationForm';
import Footer from '../../components/feature/Footer';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F3EF]">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="https://static.readdy.ai/image/b6e32af7f671036639341a9bc3afdd47/823271ed4ea397b518c9c6fd387bd621.png"
                alt="로고"
                className="h-12 w-auto object-contain"
              />
              <span className={`text-xl font-bold ${isScrolled ? 'text-[#2C4A3E]' : 'text-white'}`}>
                신원종합건설(주)
              </span>
            </div>

            <div className="hidden lg:flex items-center gap-8">
              <button
                onClick={() => scrollToSection('legalization')}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-[#2C4A3E]' : 'text-white hover:text-gray-200'
                }`}
              >
                불법건축물 양성화
              </button>
              <span className={isScrolled ? 'text-gray-400' : 'text-white/50'}>◆</span>
              <button
                onClick={() => scrollToSection('old-housing')}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-[#2C4A3E]' : 'text-white hover:text-gray-200'
                }`}
              >
                노후주택 지원사업
              </button>
              <span className={isScrolled ? 'text-gray-400' : 'text-white/50'}>◆</span>
              <button
                onClick={() => scrollToSection('solar')}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-[#2C4A3E]' : 'text-white hover:text-gray-200'
                }`}
              >
                태양광설치
              </button>
              <span className={isScrolled ? 'text-gray-400' : 'text-white/50'}>◆</span>
              <button
                onClick={() => scrollToSection('diagnosis')}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-[#2C4A3E]' : 'text-white hover:text-gray-200'
                }`}
              >
                긴급진단
              </button>
              <span className={isScrolled ? 'text-gray-400' : 'text-white/50'}>◆</span>
              <button
                onClick={() => scrollToSection('process')}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-[#2C4A3E]' : 'text-white hover:text-gray-200'
                }`}
              >
                신청절차
              </button>
              <span className={isScrolled ? 'text-gray-400' : 'text-white/50'}>◆</span>
              <button
                onClick={() => scrollToSection('cases')}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-[#2C4A3E]' : 'text-white hover:text-gray-200'
                }`}
              >
                사례연구
              </button>
            </div>

            <button
              onClick={() => scrollToSection('consultation')}
              className="bg-[#5C4033] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#4a3329] transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              무료 상담 신청
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <Hero scrollToConsultation={() => scrollToSection('consultation')} />
      <ServiceSelection
        scrollToLegalization={() => scrollToSection('legalization')}
        scrollToOldHousing={() => scrollToSection('old-housing')}
        scrollToSolar={() => scrollToSection('solar')}
      />
      <div id="legalization">
        <EmergencyDiagnosis />
      </div>
      <div id="diagnosis">
        <EmergencyDiagnosis />
      </div>
      <div id="cases">
        <CaseStudies />
      </div>
      <div id="process">
        <ProcessGuide />
      </div>
      <WhyNow scrollToConsultation={() => scrollToSection('consultation')} />
      <div id="old-housing">
        <OldHousingSupport />
      </div>
      <div id="solar">
        {/* Solar Panel Section - Placeholder for future content */}
        <section className="py-20 bg-gradient-to-br from-[#FFF9E6] to-[#FFF3CC]">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <div className="flex justify-center mb-6">
                <div className="w-24 h-24 flex items-center justify-center">
                  <i className="ri-sun-line text-8xl text-[#D97706]"></i>
                </div>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#D97706] mb-6">
                옥상에 태양광설치
              </h2>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                친환경 에너지로 전환하고 전기료를 절감하세요. 정부 지원금과 함께 태양광 설치를 시작할 수 있습니다.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-white rounded-2xl p-8 text-center shadow-lg">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 flex items-center justify-center">
                    <i className="ri-money-dollar-circle-line text-5xl text-[#D97706]"></i>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">전기료 절감</h3>
                <p className="text-gray-600 text-sm">
                  월 평균 30-50% 전기료 절감 효과
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 text-center shadow-lg">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 flex items-center justify-center">
                    <i className="ri-government-line text-5xl text-[#D97706]"></i>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">정부 지원금</h3>
                <p className="text-gray-600 text-sm">
                  설치 비용의 최대 70% 지원
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 text-center shadow-lg">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 flex items-center justify-center">
                    <i className="ri-leaf-line text-5xl text-[#D97706]"></i>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">친환경 에너지</h3>
                <p className="text-gray-600 text-sm">
                  탄소 배출 감소 및 환경 보호
                </p>
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={() => scrollToSection('consultation')}
                className="bg-[#D97706] text-white px-10 py-4 rounded-full text-lg font-bold hover:bg-[#B45309] transition-all duration-300 hover:scale-105 whitespace-nowrap inline-flex items-center gap-2"
              >
                태양광 설치 상담 신청
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </section>
      </div>
      <div id="consultation">
        <ConsultationForm />
      </div>
      <Footer />
    </div>
  );
}
